import { Component, type ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo): void {
    (async () => {
      let userId = '00000000-0000-0000-0000-000000000000';
      try {
        const { data } = await supabase.auth.getUser();
        if (data?.user?.id) userId = data.user.id;
      } catch {
        /* use fallback */
      }
      supabase
        .from('agent_actions')
        .insert({
          user_id: userId,
          agent_name: 'error-boundary',
          triggered_by: 'mcp_call' as const,
          tool_name: 'react-error',
          input: { message: error.message, stack: error.stack?.slice(0, 500) },
          output: { componentStack: info.componentStack?.slice(0, 500) },
        })
        .select('id')
        .then(
          () => {},
          () => {
            /* logging never blocks the render */
          },
        );
    })();
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-8 text-center shadow-lg">
          <div className="gradient-primary mx-auto flex h-12 w-12 items-center justify-center rounded-xl">
            <span className="text-2xl font-bold text-white">!</span>
          </div>
          <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
          {this.state.error?.message && (
            <p className="text-sm text-muted-foreground">{this.state.error.message}</p>
          )}
          <button
            onClick={this.handleRetry}
            className="mt-2 inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              window.location.reload();
            }}
            className="inline-flex items-center justify-center text-sm text-muted-foreground transition-colors hover:text-foreground"
          >
            Reload page
          </a>
        </div>
      </div>
    );
  }
}
